/**
 * dateFormat.ts — Zelium AI date/time formatting utilities
 *
 * ## Standard Formatting Rules
 *
 * | Function              | Output example                      | When to use                                                |
 * |-----------------------|-------------------------------------|------------------------------------------------------------|
 * | formatDate            | "Mar 1, 2026"                       | Fixed/important dates (billing deadlines, chart axes)      |
 * | formatTime            | "2:30 PM"                           | Time-only display                                          |
 * | formatDateTime        | "Mar 1, 2026, 2:30 PM"              | Events where exact time matters (webhooks, scheduled jobs) |
 * | formatRelativeOrDate  | "2h ago" / "yesterday" / "Mar 1"    | Recent activity (last scan, last scored, last generated)   |
 * | formatRelativeTime    | "just now" / "3 days ago" / "Mar 1" | Full relative — activity feeds, notification history       |
 * | formatDuration        | "2h 30m" / "45m" / "< 1m"          | Job/scan elapsed durations                                 |
 * | formatCountdown       | "2h 30m remaining"                  | Cooldown timers, time until next available                 |
 * | formatFutureDateTime  | "Mar 2, 2026, 3:00 PM"              | Scheduled/future dates (next feed regeneration)            |
 *
 * ## Rules
 * - Never use `toLocaleDateString()`, `toLocaleString()`, or `toLocaleTimeString()` directly in JSX.
 * - Never hardcode locale strings or date format patterns outside this file.
 * - All timestamps from the API are ISO 8601 strings — pass them directly to these helpers.
 * - All functions accept `string | Date | null | undefined` and return a string.
 * - A null/undefined input returns the `fallback` (default: `"—"`).
 */

// ─── Locale & base formatters ────────────────────────────────────────────────

/** Accepted input type for all formatting functions. */
type DateInput = string | Date | null | undefined;

const LOCALE = 'en-AU'; // AU English: "1 Mar 2026" ordering, 12-h clock

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const dateFormatterUTC = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const timeFormatter = new Intl.DateTimeFormat(LOCALE, {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const relativeFormatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toDate(value: DateInput): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function elapsedMs(date: Date): number {
  return Date.now() - date.getTime();
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isYesterday(date: Date): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameCalendarDay(date, yesterday);
}

function isYesterdayUTC(date: Date): boolean {
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  return (
    date.getUTCFullYear() === yesterday.getUTCFullYear() &&
    date.getUTCMonth() === yesterday.getUTCMonth() &&
    date.getUTCDate() === yesterday.getUTCDate()
  );
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Format a date only (no time).
 * Output: "Mar 1, 2026"
 *
 * Use for: billing deadlines, score history chart axes, table date columns.
 */
export function formatDate(
  value: DateInput,
  fallback = '—',
): string {
  const d = toDate(value);
  return d ? dateFormatter.format(d) : fallback;
}

/**
 * Format a time only (no date).
 * Output: "2:30 PM"
 *
 * Use for: time-only labels when date is shown separately.
 */
export function formatTime(
  value: DateInput,
  fallback = '—',
): string {
  const d = toDate(value);
  return d ? timeFormatter.format(d) : fallback;
}

/**
 * Format a full date + time.
 * Output: "Mar 1, 2026, 2:30 PM"
 *
 * Use for: events where the precise moment matters — webhook last reconciliation,
 * next scheduled feed regeneration, auto-fix applied timestamps.
 */
export function formatDateTime(
  value: DateInput,
  fallback = '—',
): string {
  const d = toDate(value);
  return d ? dateTimeFormatter.format(d) : fallback;
}

/**
 * Format a future date + time.
 * Alias for formatDateTime with a semantically-clear name.
 * Output: "Mar 2, 2026, 3:00 PM"
 *
 * Use for: scheduled or future timestamps (next feed regeneration, next retry, trial end date+time).
 */
export function formatFutureDateTime(
  value: DateInput,
  fallback = '—',
): string {
  return formatDateTime(value, fallback);
}

/**
 * Format as relative time for recent events; fall back to date for older ones.
 *
 * | Age              | Output            |
 * |------------------|-------------------|
 * | < 60 seconds     | "just now"        |
 * | < 60 minutes     | "23m ago"         |
 * | < 24 hours       | "3h ago"          |
 * | yesterday        | "yesterday"       |
 * | older            | "Mar 1, 2026"     |
 *
 * Use for: "Last Scan", "Last scored", "Last generated", "Last seen" — any recent-activity label
 * where the user needs a quick sense of recency but an exact date for older items.
 */
export function formatRelativeOrDate(
  value: DateInput,
  fallback = '—',
): string {
  const d = toDate(value);
  if (!d) return fallback;

  const ms = elapsedMs(d);

  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) {
    const mins = Math.floor(ms / 60_000);
    return `${mins}m ago`;
  }
  if (ms < 86_400_000) {
    const hours = Math.floor(ms / 3_600_000);
    return `${hours}h ago`;
  }
  if (isYesterday(d)) return 'yesterday';

  return dateFormatter.format(d);
}

/**
 * UTC variant of formatRelativeOrDate.
 *
 * Identical recency buckets ("just now" / "23m ago" / "3h ago"), but the
 * day-boundary ("yesterday") and absolute-date fallback are computed in UTC.
 *
 * Use when the displayed date must agree with a UTC-bucketed value elsewhere —
 * e.g. the crawler "Last Seen" column lining up with the UTC daily-trend dates.
 */
export function formatRelativeOrDateUTC(
  value: DateInput,
  fallback = '—',
): string {
  const d = toDate(value);
  if (!d) return fallback;

  const ms = elapsedMs(d);

  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) {
    const mins = Math.floor(ms / 60_000);
    return `${mins}m ago`;
  }
  if (ms < 86_400_000) {
    const hours = Math.floor(ms / 3_600_000);
    return `${hours}h ago`;
  }
  if (isYesterdayUTC(d)) return 'yesterday';

  return dateFormatterUTC.format(d);
}

/**
 * Full relative time — uses Intl.RelativeTimeFormat for natural language.
 * Falls back to absolute date beyond 7 days.
 *
 * | Age              | Output              |
 * |------------------|---------------------|
 * | < 60 seconds     | "just now"          |
 * | < 60 minutes     | "23 minutes ago"    |
 * | < 24 hours       | "3 hours ago"       |
 * | 1 day            | "yesterday"         |
 * | 2–7 days         | "3 days ago"        |
 * | older            | "Mar 1, 2026"       |
 *
 * Use for: notification feeds, activity history lists, audit logs.
 */
export function formatRelativeTime(
  value: DateInput,
  fallback = '—',
): string {
  const d = toDate(value);
  if (!d) return fallback;

  const ms = elapsedMs(d);

  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) {
    return relativeFormatter.format(-Math.floor(ms / 60_000), 'minute');
  }
  if (ms < 86_400_000) {
    return relativeFormatter.format(-Math.floor(ms / 3_600_000), 'hour');
  }

  const days = Math.floor(ms / 86_400_000);
  if (days <= 7) {
    return relativeFormatter.format(-days, 'day');
  }

  return dateFormatter.format(d);
}

/**
 * Format a duration in milliseconds to a human-readable string.
 * Output: "2h 30m" | "45m" | "< 1m" | "—"
 *
 * Use for: job, scan, or operation elapsed durations.
 */
export function formatDuration(ms: number | null | undefined, fallback = '—'): string {
  if (ms == null || ms < 0) return fallback;
  if (ms < 60_000) return '< 1m';

  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/**
 * Format the duration between two timestamps (e.g. job startedAt → completedAt).
 * Output: "2h 30m" | "45m" | "< 1m" | "—"
 */
export function formatElapsed(
  start: DateInput,
  end: DateInput,
  fallback = '—',
): string {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return fallback;
  return formatDuration(e.getTime() - s.getTime(), fallback);
}

/**
 * Format a countdown from now until a future date.
 * Output: "2h 30m remaining" | "< 1m remaining" | "—"
 *
 * Use for: rescan cooldown timers, rate-limit retry-after displays.
 */
export function formatCountdown(
  until: Date | null | undefined,
  fallback = '—',
): string {
  if (!until) return fallback;
  const ms = until.getTime() - Date.now();
  if (ms <= 0) return fallback;
  const dur = formatDuration(ms);
  return dur === fallback ? fallback : `${dur} remaining`;
}
